/**
 * #759 D18 — cómo se le avisa a una prueba o a un comp de que se le acaba. Lo usan el aviso
 * flotante, la tarjeta de la barra lateral, el avatar y su menú, Configuración → Facturación y la
 * página de planes, para que digan lo mismo y a la vez.
 */
import { lighten } from '@mui/material/styles';

/** El aviso por días que toca: el más cercano de los ya alcanzados (7 → 1), o null si aún no. */
export function reachedTimeWarning(freeAccess) {
  if (!freeAccess) return null;
  const reached = freeAccess.warningDays.filter((day) => freeAccess.daysLeft <= day);
  return reached.length > 0 ? Math.min(...reached) : null;
}

/**
 * Ya está en sus días de aviso (comp a 7, prueba a 3): toca elegir plan, y se dice como el plan
 * agotado (#1300), con el triángulo rojo del avatar.
 */
export function isEndingSoon(freeAccess) {
  return reachedTimeWarning(freeAccess) !== null;
}

/** «Te quedan N días de prueba», en el idioma de la interfaz: `t` es el de `useTranslation`. */
export function daysLeftTitle(t, daysLeft) {
  if (daysLeft <= 0) return t('billing.freeAccess.endsToday', 'Tu prueba termina hoy');
  return t('billing.freeAccess.daysLeft', {
    count: daysLeft,
    defaultValue: daysLeft === 1 ? 'Te queda 1 día de prueba' : 'Te quedan {{count}} días de prueba',
  });
}

/** Ámbar mientras le queda tiempo; rojo en sus días de aviso. */
export function freeAccessTone(freeAccess) {
  return isEndingSoon(freeAccess) ? 'error' : 'warning';
}

/** Qué parte del periodo le queda, de 0 a 1: lo que pintan el anillo del avatar y la barra. */
export function daysLeftFraction(freeAccess) {
  if (!freeAccess?.totalDays) return 1;
  return Math.min(1, Math.max(0, freeAccess.daysLeft / freeAccess.totalDays));
}

/**
 * El color de las marcas de aviso (anillo, iconos, triángulo): el del tono, suavizado. El rojo del
 * tema, tal cual, era chillón. Solo para marcas, nunca para texto: suavizado no da el contraste.
 */
export function softToneColor(theme, tone) {
  return lighten(theme.palette[tone].main, 0.3);
}

/**
 * El botón «Elegir un plan» de una prueba o un comp, en el mismo rojo suave que sus marcas. Va en
 * `sx` y no en `color`: MUI solo admite colores de la paleta, no uno derivado.
 */
export function choosePlanButtonSx(theme) {
  return {
    bgcolor: softToneColor(theme, 'error'),
    color: theme.palette.common.white,
    '&:hover': { bgcolor: lighten(theme.palette.error.main, 0.15) },
  };
}
